import { KeyboardArrowDown, Notes } from '@mui/icons-material'
import { Accordion, AccordionSummary, Box, Typography, AccordionDetails } from '@mui/material'
import { ResourceLayers } from '../../../assets/svgs/ResourceLayers'
import { RatingRadio } from '../../customComponents/RatingRadio'
import { TextArea } from '../../customComponents/TextArea'
import { BlueButton } from '../../customComponents/buttons/BlueButton'
import { ProfileImage } from '../../customComponents/user/ProfileImage'
import { type CETASnapshotIndicator, type CETAReconcileAssessmentIndicator } from '../../../models/CETAReconcileAssessment'
import { useState } from 'react'
import { SaveReconcileIndicator } from '../../../services/CETAClassroomService'
import { type CETAReconcileIndicatorRequest } from '../../../models/CETAReconcileIndicatorRequest'
import { toast } from 'react-toastify'
import { BlockButton } from '../../customComponents/buttons/BlockButton'

interface Props {
  isSelected: boolean
  indicator: CETAReconcileAssessmentIndicator
  readOnly: boolean
  highlightIndicators: boolean
  forceShowNotes?: boolean
  onSaveIndicatorRating: (indicator: CETAReconcileIndicatorRequest) => void
  onClick: () => void
  onNotesOrRatingChanged: (indicatorId: number, hasChanges: boolean) => void
}

export const IndicatorReview = (props: Props): JSX.Element => {
  const [notes, setNotes] = useState<string>(props.indicator.notes)
  const [rating, setRating] = useState<number | undefined>(props.indicator.rating)
  const [showNotes, setShowNotes] = useState<boolean>(props.forceShowNotes === true)

  const confirmResponse = async (indicatorId: number): Promise<void> => {
    if (rating == null) {
      toast.error('Please select a rating')
      return
    }

    const indicatorRequest: CETAReconcileIndicatorRequest = { id: indicatorId, rating, notes }
    await SaveReconcileIndicator(indicatorRequest)
    toast.success('Response saved')
    props.onSaveIndicatorRating(indicatorRequest)
    props.onClick()
  }

  const updateRating = (value: number): void => {
    setRating(value)
    props.onNotesOrRatingChanged(props.indicator.id, true)
  }

  const updateNotes = (value: string): void => {
    if (value === props.indicator.notes && rating === props.indicator.rating) {
      props.onNotesOrRatingChanged(props.indicator.id, false)
      return
    }
    setNotes(value)
    props.onNotesOrRatingChanged(props.indicator.id, true)
  }

  const clearRatingAndNotes = (): void => {
    setNotes(props.indicator.notes)
    setRating(props.indicator.rating)
    props.onNotesOrRatingChanged(props.indicator.id, false)
    props.onClick()
  }

  const showButtons = (): void => {
    props.onNotesOrRatingChanged(props.indicator.id, true)
  }

  const ratingAverage = (submittedIndicators: CETASnapshotIndicator[]): number => {
    const completedIndicators = submittedIndicators.filter(i => i.rating != null)

    if (completedIndicators.length === 0) {
      return 0
    }

    const total = completedIndicators.reduce((acc, curr) => acc + (curr.rating ?? 0), 0)
    return Math.round(total / completedIndicators.length * 1000) / 1000
  }

  return <Accordion
    expanded={props.isSelected}
    data-testid={`indicator-${props.indicator.id}`}
    sx={{
      marginBottom: '.5em',
      backgroundColor: props.isSelected ? 'var(--light-blue-100)' : 'var(--grey-100)',
      border: props.highlightIndicators && props.indicator.rating == null ? '1px solid red' : '1px solid var(--border)',
      borderRadius: '6px'
    }}
  >
    <AccordionSummary expandIcon={<KeyboardArrowDown />} onClick={props.onClick} role='none presentation' aria-expanded={undefined}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Box display='flex' alignItems='center'>
          <ResourceLayers hidden={true}/>
          <Typography variant='subtitle1' ml='.5em'>{props.indicator.name}</Typography>
        </Box>

        { (props.isSelected || rating != null) && <RatingRadio
          initialValue={rating}
          readOnly={props.readOnly || !props.isSelected}
          onValueSelected={val => { updateRating(val) }}
          onClick={(e) => { e.stopPropagation() }}
        />
        }
      </Box>
    </AccordionSummary>

    <AccordionDetails>
      <Box sx={{ backgroundColor: 'var(--light-blue-50)', p: '1em', borderRadius: '6px' }}>
        <Box sx={{ display: 'flex' }}>
          {props.indicator.submittedIndicators.map((submittedIndicator, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: '.5em' }}>
              <ProfileImage
                numberOfLetters={2}
                name={submittedIndicator.firstName.charAt(0) + submittedIndicator.lastName.charAt(0)}
                imageKey={submittedIndicator.imageKey}
              />

              <Typography variant='body1' data-testid={`submittedIndicatorRating-${submittedIndicator.lastName}`}>{submittedIndicator.rating ?? '-'}</Typography>
            </Box>
          ))}

          <Typography variant='body1' alignContent='center' mx='2em' data-testid='averageRating'>Avg: {ratingAverage(props.indicator.submittedIndicators)}</Typography>

          <Notes sx={{ alignSelf: 'center', cursor: 'pointer' }} onClick={() => { setShowNotes(!showNotes) }} data-testid='showNotes' />
        </Box>

        {showNotes && props.indicator.submittedIndicators.map((submittedIndicator, index) => (
          <Box key={index}>
            {submittedIndicator.notes !== '' &&
              <Box sx={{ backgroundColor: 'white', mt: '.5em', p: '.5em', borderRadius: '6px' }} data-testid='submittedNotes'>
                <Typography variant='body1' fontWeight='600'>{submittedIndicator.firstName} {submittedIndicator.lastName} -</Typography>
                <Typography variant='body1'>{submittedIndicator.notes}</Typography>
              </Box>
            }
          </Box>
        ))}

        {showNotes && props.indicator.submittedIndicators.every(i => i.notes === '') &&
          <Typography variant='body1' sx={{ backgroundColor: 'white', mt: '.5em', p: '.5em', borderRadius: '6px' }}>
            No notes were provided
          </Typography>
        }
      </Box>

      <Typography variant='body1' my='1em'>{props.indicator.description}</Typography>

      <Box sx={{ display: 'flex' }}>
        <TextArea
          onFocus={showButtons}
          initialValue={notes}
          readOnly={props.readOnly}
          name='indicatorNotes'
          placeholder='Final Indicator notes go here'
          inputSx={{ backgroundColor: 'white' }}
          sx={{ width: '100%' }}
          onBlur={(e) => { updateNotes(e.target.value) }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {(!props.readOnly && (props.indicator.hasPendingUpdates === true)) && <>
          <BlockButton sx={{ height: '4em', width: '10em', mt: '1em' }} onClick={clearRatingAndNotes} dataTestId='returnButton'>Cancel</BlockButton>
          <BlueButton
            onClick={async () => { await confirmResponse(props.indicator.id) }}
            dataTestId='confirmResponse'
            sx={{ height: '4em', width: '14em', mt: '1em' }}
          >
            { props.indicator.rating != null ? 'Change Response' : 'Confirm Response' }
          </BlueButton>
        </>
        }
      </Box>
    </AccordionDetails>
  </Accordion>
}
